/**
 * UI state store.
 *
 * Two concerns live here and only here: the active locale and the colour-scheme preference. Both are *client*
 * preferences that must survive a reload, so they are persisted; both are also mirrored to the account profile
 * once the user signs in, and the profile wins (see `usePreferencesSync`).
 *
 * The access token deliberately does **not** live in local storage (ADR-0011): it stays in the auth store in
 * memory and is re-acquired with the `HttpOnly` refresh cookie on reload.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { DigitStyle } from '@/lib/format/numbers';
import type { ThemePreference } from '@/types/api';

/** Locales the interface ships with. */
export const SUPPORTED_LOCALES = ['fa', 'en'] as const;

/** A locale the interface can render. */
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

/** Preference accepted by {@link useUiStore.setState}: the profile value or the "follow the OS" option. */
export type ResolvedTheme = 'light' | 'dark';

interface UiState {
  /** Active interface locale. Persian is the default product language (ADR-0012 §1). */
  locale: AppLocale;
  /** Preferred colour scheme; `System` follows `prefers-color-scheme`. */
  theme: ThemePreference;
  /** Whether the sidebar is collapsed to its icon rail on desktop. */
  sidebarCollapsed: boolean;
  /** Whether the mobile navigation drawer is open. */
  mobileNavOpen: boolean;
  /** Sets the interface locale. */
  setLocale: (locale: AppLocale) => void;
  /** Sets the colour-scheme preference. */
  setTheme: (theme: ThemePreference) => void;
  /** Collapses or expands the desktop sidebar. */
  setSidebarCollapsed: (collapsed: boolean) => void;
  /** Toggles the desktop sidebar. */
  toggleSidebar: () => void;
  /** Opens or closes the mobile navigation drawer. */
  setMobileNavOpen: (open: boolean) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      locale: 'fa',
      theme: 'System',
      sidebarCollapsed: false,
      mobileNavOpen: false,

      setLocale: (locale) => set({ locale }),

      setTheme: (theme) => set({ theme }),

      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),

      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setMobileNavOpen: (mobileNavOpen) => set({ mobileNavOpen }),
    }),
    {
      name: 'prompt-forge.ui',
      // Transient layout state is intentionally excluded, so a reload always starts with both panels closed.
      partialize: (state) => ({ locale: state.locale, theme: state.theme }),
      version: 1,
    },
  ),
);

/**
 * Resolves the effective colour scheme.
 *
 * Kept as a pure function rather than a computed field inside the store so it can be unit tested without a DOM.
 *
 * @param theme Stored preference.
 * @param systemPrefersDark Whether the operating system currently reports a dark appearance.
 * @returns The scheme to apply to the document.
 */
export function resolveTheme(theme: ThemePreference, systemPrefersDark: boolean): ResolvedTheme {
  if (theme === 'Dark') {
    return 'dark';
  }

  if (theme === 'Light') {
    return 'light';
  }

  return systemPrefersDark ? 'dark' : 'light';
}

/**
 * Returns the numeral family that matches a locale.
 *
 * Persian pages show Persian numerals (ADR-0012 §7); English pages show Latin ones. Identifiers are excluded from
 * this rule and always stay Latin — see `LtrText` and `NumericValue`.
 *
 * @param locale Active interface locale.
 * @returns The digit style to use for quantities.
 */
export function defaultDigitPreference(locale: AppLocale): DigitStyle {
  return locale === 'fa' ? 'persian' : 'latin';
}
